import React, { useEffect, useState } from 'react';
import { Table, Button, Flex, Breadcrumb, Form, message, Popconfirm, Tag, Space, Pagination } from 'antd';
import { createStyles } from 'antd-style';
import { EyeOutlined, HomeOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { apiRequest } from '../../../service/apiRequest';
import ThreadModal from './ThreadModal';
import TicketModal from './TicketModal';
import { showSuccess, showError } from '../../../components/errorHandler';
import type { PopconfirmProps } from 'antd';
import { useCurrentModule } from '../../../hooks/useCurrentModule';



const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface Ticket {
  ticket_id: number;
  ticket_ticket_status: string;
  ticket_title: string;
  ticket_type: string;
  ticket_description: string;
  ticket_created_on: string;
}

interface DataType {
  key: number;
  ticket_id: number;
  ticket_ticket_status: string;
  ticket_number: string;
  details: string;
  ticket_name: string;
  ticket_type: string;
  description: string;
  created_on: string;
}

const Tickets: React.FC = () => {
  const { styles } = useStyle();
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const currentModule = useCurrentModule();

  // console.log(currentModule);

  const confirm = (ticket_id: Number, ticket_ticket_status: String) => {
    console.log('Confirmed for:', ticket_id);
    handleTicketStatusUpdate({ ticket_id: ticket_id, ticket_ticket_status: ticket_ticket_status });
  };

  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const handleTicketStatusUpdate = async (values: any) => {
    try {
      const ticketStatusUpdate = await apiRequest({
        method: 'post',
        url: 'update-ticket-status',
        data: {
          ticket_ticket_status: values.ticket_ticket_status,
          ticket_id: values.ticket_id,
        },
      });

      getTickets();

      showSuccess(ticketStatusUpdate.message || 'Ticket status update successfully!');
    } catch (error) {
      showError('Failed to create ticket');
    }
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const handleEditClick = (ticketId: number) => {
    setSelectedTicketId(ticketId);
    setOpenModal(true);
  };


  const handleCreate = () => {
    getTickets();
  };

  const showCreateModal = () => {
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
  };

  const getTickets = async () => {
    try {
      const ticketData = await apiRequest<Ticket[]>({
        method: 'get',
        url: 'tickets',
      });
      setTickets(ticketData);
    } catch (error) {
      showError('Failed to fetch tickets');
      console.error(error);
    }
  };

  useEffect(() => {
    getTickets();
  }, []);

  const ticketsData: DataType[] = tickets.map((ticket) => ({
    key: ticket.ticket_id,
    ticket_id: ticket.ticket_id,
    ticket_ticket_status: ticket.ticket_ticket_status,
    ticket_number: `#-${ticket.ticket_id}`,
    details: ticket.ticket_ticket_status,
    ticket_name: ticket.ticket_title,
    ticket_type: ticket.ticket_type,
    description: ticket.ticket_description,
    created_on: new Date(ticket.ticket_created_on).toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      dateStyle: 'medium',
      timeStyle: 'short',
    }),
  }));

  const columns = [
    {
      title: 'Ticket Number',
      dataIndex: 'ticket_number',
      render: (_: string, record: DataType) => (
        <>
          <div>#{record.ticket_id}</div>
          <Tag
            style={{
              borderRadius: '10px',
              fontSize: '12px',
              padding: '0 10px',
              height: '23px',
              lineHeight: '20px',
              // color: record.ticket_ticket_status === 'Open' ? 'green' : 'red',
              color: 'white',
              borderColor: record.ticket_ticket_status === 'Open' ? 'green' : 'red',
              backgroundColor: record.ticket_ticket_status === 'Open' ? 'green' : 'red',
              // backgroundColor: 'transparent',
            }}
          >
            {record.ticket_ticket_status}
          </Tag>

          {/* <div>{record.ticket_ticket_status}</div> */}
        </>
      ),
      width: 100,
    },
    {
      title: 'Details',
      dataIndex: 'details',
      width: 150,
      render: (_: string, record: DataType) => (
        <>
          <div>{record.ticket_name}</div>
          <div>{record.ticket_type}</div>
        </>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      width: 300,
      render: (text: string) => (
        <span>
          {text.length > 100 ? `${text.slice(0, 100)}...` : text}
        </span>
      ),
    },
    {
      title: 'Created On',
      dataIndex: 'created_on',
      width: 120,
    },
    {
      title: 'Action',
      dataIndex: 'action',
      width: 120,
      render: (_: any, record: DataType) => (
        <>
          <center>
            {record.ticket_ticket_status !== 'Close' && currentModule?.right_edit === 'Y' && (
              <Popconfirm
                title="Close the ticket"
                description="Are you sure to close this ticket?"
                onConfirm={() => confirm(record.ticket_id, 'Close')}
                onCancel={cancel}
                okText="Yes Confirm"
                cancelText="No">
                <Button danger style={{
                  width: '100%',
                }}>Close Ticket</Button>
              </Popconfirm>
            )}
            {record.ticket_ticket_status == 'Close' && currentModule?.right_edit === 'Y' && (
              <Popconfirm
                title="Reopen the ticket"
                description="Are you sure want to reopen this ticket?"
                onConfirm={() => confirm(record.ticket_id, 'Open')}
                onCancel={cancel}
                okText="Yes Confirm"
                cancelText="No">
                <Button color="cyan" variant="outlined" style={{
                  width: '100%',
                }}>Reopen Ticket</Button>
              </Popconfirm>
            )}
            <Button
              type="primary"
              icon={<EyeOutlined />}
              onClick={() => handleEditClick(record.key)}
              style={{
                width: '100%',
                marginTop: 4
              }}>
              View Thread
            </Button>
          </center>
        </>
      ),

    },
  ];




  return (
    <>
      <Space style={{ display: 'flex', marginBottom: '16px', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
        {/* Breadcrumb in the header */}
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Tickets</Breadcrumb.Item>
        </Breadcrumb>

        {/* Button aligned to the right */}
        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Create New Ticket
            </Button>
          </Flex>
        )}
      </Space>

      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >


        <Table<DataType>
          className={styles.customTable}
          columns={columns}
          dataSource={ticketsData.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 13 }}
          locale={{ emptyText: 'No tickets found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, tickets.length)} of {tickets.length}
              </span>
              <Pagination
                {...pagination}
                total={tickets.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />

        {/* Ticket Modal */}
        <TicketModal
          visible={isModalVisible}
          onCancel={hideCreateModal}
          onCreate={handleCreate}
        />

        {/* Modal: View Thread */}
        <ThreadModal
          ticketId={selectedTicketId}
          onClose={() => setOpenModal(false)}
          visible={openModal}
        />
      </div>
    </>
  );
};

export default Tickets;